import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Plus, Search, FileText, MessageSquare, Paperclip, Send, X, Building2, Check, Link2, AlertTriangle,
  Zap, RefreshCw, XCircle, CheckCircle2, MinusCircle,
} from 'lucide-react';
import AppLayout from '../components/AppLayout';
import { useAuth } from '../contexts/AuthProvider';
import PetanqueLoader from '../components/PetanqueLoader';
import ProformaDetailDialog from '../components/ProformaDetailDialog';
import BulkPayDialog from '../components/BulkPayDialog';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select } from '../components/ui/select';
import { Dialog } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { api } from '../api/sangly';
import { moneyExact, fmtDate, timeAgo, CURRENCIES, PROFORMA_STATUS } from '../lib/format';
import { cn } from '../lib/utils';

const TABS = [
  { key: 'active', label: 'To pay' },
  { key: 'to_finalize', label: 'To finalize' },
  { key: 'archived', label: 'Archived' },
];
const EMPTY = {
  active: 'Nothing to pay — add a pro-forma with the button above.',
  to_finalize: 'Nothing to finalize — paid pro-formas land here until the vendor has been told.',
  archived: 'Nothing archived yet.',
};
const ACCEPT = 'application/pdf,image/png,image/jpeg,image/gif,image/webp';

const today = () => new Date().toISOString().slice(0, 10);
// Pending + due date in the past → the vendor is waiting on us.
const isLate = (pf) => pf.status === 'pending' && pf.due_date && pf.due_date < today();

export default function Proformas() {
  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState('active');
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ active: 0, to_finalize: 0, archived: 0 });
  const [selected, setSelected] = useState(() => new Set());   // ticked for "Pay all" (payers)
  const [bulk, setBulk] = useState(null);                       // rows being paid in the queue
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);       // false | true | { id, name } (prefilled vendor)
  const [q, setQ] = useState('');
  const [users, setUsers] = useState([]);
  // Vendors in the "Proforma" account status (Suppliers page) that have no
  // pending pro-forma here - the two views should agree.
  const [unlogged, setUnlogged] = useState([]);

  const load = useCallback(async (soft = false) => {
    if (!soft) setLoading(true);
    try {
      const [active, toFinalize, archived, vendors] = await Promise.all([
        api.proformas.list('active'), api.proformas.list('to_finalize'), api.proformas.list('archived'),
        api.suppliers.list({ filter: 'proforma', all: 1 }).then((d) => d.results || []).catch(() => []),
      ]);
      setCounts({ active: active.length, to_finalize: toFinalize.length, archived: archived.length });
      setRows(tab === 'archived' ? archived : tab === 'to_finalize' ? toFinalize : active);
      setSelected((sel) => new Set([...sel].filter((id) => active.some((r) => r.id === id))));
      const covered = new Set(active.map((r) => r.supplier).filter(Boolean));
      setUnlogged(vendors.filter((v) => v.account_status === 4 && !covered.has(v.id)));
    } finally { setLoading(false); }
  }, [tab]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.users.list().then(setUsers).catch(() => {}); }, []);

  const visible = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) => [r.supplier_name, r.their_reference, r.our_reference, r.remarks]
      .some((v) => (v || '').toLowerCase().includes(s)));
  }, [rows, q]);

  const total = useMemo(() => visible.reduce((acc, r) => {
    acc[r.currency] = (acc[r.currency] || 0) + Number(r.amount || 0);
    return acc;
  }, {}), [visible]);

  // "Pay all": payers tick rows on the To-pay tab; only payable ones can be ticked
  const canBulk = !!user?.can_pay && tab === 'active';
  const payable = (r) => r.status === 'pending' && Number(r.amount) > 0 && !r.in_payment;
  const payableVisible = visible.filter(payable);
  const allTicked = payableVisible.length > 0 && payableVisible.every((r) => selected.has(r.id));
  const toggleAll = () => setSelected(allTicked ? new Set() : new Set(payableVisible.map((r) => r.id)));
  const toggle = (id) => setSelected((sel) => { const n = new Set(sel); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const selectedRows = rows.filter((r) => selected.has(r.id));
  const selectedTotal = Object.entries(selectedRows.reduce((acc, r) => { acc[r.currency] = (acc[r.currency] || 0) + Number(r.amount); return acc; }, {}))
    .map(([c, a]) => moneyExact(a, c)).join(' + ');

  return (
    <AppLayout
      title="Pro-forma"
      description="Pro-forma invoices to pay up front — not in Odoo yet. Log them here, discuss, and mark them paid."
      actions={user?.can_pay ? <AirwallexCheck /> : null}
    >
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="flex gap-1.5">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={cn('inline-flex items-center gap-1.5 rounded-md border px-3 py-1 text-[13px] font-medium transition-colors',
                tab === t.key ? 'border-primary bg-primary/12 text-foreground' : 'border-border bg-card text-muted-foreground hover:bg-muted')}>
              {t.label}
              <span className="rounded-full bg-primary/15 px-1.5 text-[11px] font-semibold tabular-nums text-primary">
                {counts[t.key]}
              </span>
            </button>
          ))}
        </div>
        <div className="relative ml-auto">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search vendor / reference…" className="h-9 w-56 pl-8" />
        </div>
        {canBulk && selected.size > 0 && (
          <Button variant="secondary" onClick={() => setBulk(selectedRows)} title="Pay the ticked pro-formas via Airwallex, one after the other">
            <Zap className="h-4 w-4 text-primary" /> Pay {selected.size} selected{selectedTotal ? ` · ${selectedTotal}` : ''}
          </Button>
        )}
        <Button onClick={() => setAdding(true)}>
          <Plus className="h-4 w-4" /> Add pro-forma
        </Button>
      </div>

      {unlogged.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-sky-200 bg-sky-500/10 px-3 py-2.5 text-[13px] text-sky-900">
          <AlertTriangle className="h-4 w-4 shrink-0 text-sky-600" />
          <span>
            <span className="font-medium">{unlogged.length} vendor{unlogged.length === 1 ? ' is' : 's are'} marked Proforma</span> in Suppliers
            without a pro-forma logged here:
          </span>
          {unlogged.map((v) => (
            <button key={v.id} type="button" onClick={() => setAdding({ id: v.id, name: v.name })}
                    title="Log the pro-forma for this vendor"
                    className="inline-flex items-center gap-1 rounded-md border border-sky-300 bg-card px-2 py-0.5 font-medium text-sky-800 hover:bg-sky-500/10">
              {v.name} <Plus className="h-3 w-3" />
            </button>
          ))}
        </div>
      )}

      <div className="overflow-hidden rounded-lg border border-border bg-card shadow-card">
        <Table className="min-w-[1040px]">
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              {canBulk && (
                <TableHead className="w-8">
                  <input type="checkbox" checked={allTicked} onChange={toggleAll} disabled={payableVisible.length === 0}
                         title="Tick all payable" className="h-3.5 w-3.5 rounded border-border accent-[hsl(var(--primary))]" />
                </TableHead>
              )}
              <TableHead>{tab === 'to_finalize' ? 'Paid on' : 'Added'}</TableHead>
              <TableHead>Vendor</TableHead>
              <TableHead>Reference</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>{tab === 'to_finalize' ? 'Paid via' : 'Deadline'}</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">{tab === 'to_finalize' ? 'Vendor told?' : ' '}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={8} className="h-40 text-center">
                <PetanqueLoader size={34} roll className="mx-auto" />
              </TableCell></TableRow>
            )}
            {!loading && visible.length === 0 && (
              <TableRow><TableCell colSpan={8} className="h-40 text-center text-muted-foreground">
                {q ? 'No pro-forma matches your search.' : EMPTY[tab]}
              </TableCell></TableRow>
            )}
            {!loading && visible.map((r) => (
              <TableRow key={r.id} className={cn('cursor-pointer hover:bg-muted/30', selected.has(r.id) && 'bg-primary/[0.04]')} onClick={() => navigate(`/proformas/${r.id}`)}>
                {canBulk && (
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} disabled={!payable(r)}
                           title={payable(r) ? 'Tick to pay in one go' : r.in_payment ? 'Already in payment' : 'Fill in the amount first'}
                           className="h-3.5 w-3.5 rounded border-border accent-[hsl(var(--primary))] disabled:opacity-40" />
                  </TableCell>
                )}
                <TableCell className="whitespace-nowrap">
                  {tab === 'to_finalize' ? (
                    <>
                      <div className="text-[13px]">{fmtDate(r.paid_at)} <span className="text-[11px] text-muted-foreground">· {timeAgo(r.paid_at)}</span></div>
                      {r.paid_by_name && <div className="text-[11px] text-muted-foreground">by {r.paid_by_name}</div>}
                    </>
                  ) : (
                    <>
                      <div className="text-[13px]">{fmtDate(r.added_date)}</div>
                      {r.added_by_name && <div className="text-[11px] text-muted-foreground">by {r.added_by_name}</div>}
                    </>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1.5 font-medium">
                    {r.supplier_name || '-'}
                    {r.supplier && <Link2 className="h-3 w-3 shrink-0 text-muted-foreground" title="Linked to the vendor" />}
                  </div>
                  {r.remarks && <div className="line-clamp-1 max-w-[260px] text-[11px] text-muted-foreground">{r.remarks}</div>}
                </TableCell>
                <TableCell className="font-mono text-[12px]">
                  {r.their_reference || r.our_reference ? (
                    <>
                      {r.their_reference && <div>{r.their_reference}</div>}
                      {r.our_reference && <div className={cn(r.their_reference && 'text-[11px] text-muted-foreground')}>{r.our_reference}</div>}
                    </>
                  ) : <span className="text-muted-foreground">-</span>}
                </TableCell>
                <TableCell className="text-right font-semibold tnum whitespace-nowrap">
                  {Number(r.amount) > 0
                    ? moneyExact(r.amount, r.currency)
                    : <span className="font-medium text-amber-600" title="Open it and fill in the amount">amount?</span>}
                </TableCell>
                <TableCell className={cn('whitespace-nowrap', isLate(r) && 'font-medium text-rose-600')}>
                  {tab === 'to_finalize' ? (
                    r.airwallex_short_reference
                      ? <span className="inline-flex items-center gap-1 text-[12px]"><Zap className="h-3.5 w-3.5 text-primary" /><span className="font-mono">{r.airwallex_short_reference}</span><span className="text-muted-foreground">· {r.airwallex_status}</span></span>
                      : <span className="text-[12px] text-muted-foreground">manual transfer</span>
                  ) : r.due_date ? fmtDate(r.due_date) : <span className="text-muted-foreground">-</span>}
                </TableCell>
                <TableCell>
                  {r.in_payment
                    ? <Badge variant="info">In payment · {r.airwallex_status}</Badge>
                    : <Badge variant={PROFORMA_STATUS[r.status]?.variant || 'default'}>{PROFORMA_STATUS[r.status]?.label || r.status}</Badge>}
                  {r.status === 'paid' && r.paid_by_name && (
                    <div className="mt-0.5 text-[11px] text-muted-foreground">by {r.paid_by_name}</div>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  {tab === 'to_finalize' ? (
                    <div className="inline-flex items-center gap-2 text-[12px]">
                      {r.airwallex_letter
                        ? <span className="inline-flex items-center gap-1 text-emerald-700" title={r.airwallex_letter.name}><FileText className="h-3.5 w-3.5" /> payment proof</span>
                        : r.airwallex_transfer_id && <span className="text-muted-foreground" title="Payment proof not fetched yet">no proof yet</span>}
                      {r.vendor_notified_at
                        ? <Badge variant="success"><Check className="h-3 w-3" /> emailed</Badge>
                        : <Badge variant="warning">to tell</Badge>}
                    </div>
                  ) : (
                  <div className="inline-flex items-center gap-2.5 text-[12px] text-muted-foreground">
                    {r.documents?.length > 0 && (
                      <span className="inline-flex items-center gap-1" title={r.documents.map((d) => d.name).join('\n')}>
                        <FileText className="h-3.5 w-3.5" /> {r.documents.length}
                      </span>
                    )}
                    {r.comment_count > 0 && (
                      <span className="inline-flex items-center gap-1" title="Notes">
                        <MessageSquare className="h-3.5 w-3.5" /> {r.comment_count}
                      </span>
                    )}
                  </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-x-4 text-[12px] text-muted-foreground">
        <span>{visible.length} pro-forma{visible.length === 1 ? '' : 's'}</span>
        {Object.entries(total).map(([cur, amt]) => (
          <span key={cur} className="tnum">total {moneyExact(amt, cur)}</span>
        ))}
      </div>

      <AddProformaDialog open={!!adding} initialVendor={typeof adding === 'object' ? adding : null}
        onClose={() => setAdding(false)}
        onCreated={(pf) => { setAdding(false); setTab('active'); load(true); navigate(`/proformas/${pf.id}`); }} />

      <ProformaDetailDialog proformaId={id ? Number(id) : null} open={!!id} users={users}
        onClose={() => navigate('/proformas')} onChanged={() => load(true)} />

      <BulkPayDialog open={!!bulk} items={bulk || []} onClose={() => setBulk(null)}
        onDone={() => { setSelected(new Set()); load(true); }} />
    </AppLayout>
  );
}

/**
 * Vendor picker: searches Sangly's suppliers as you type, then Odoo vendors that
 * aren't in Sangly yet (picking one pulls it in, like "Add vendor" on the
 * Suppliers page). Typing without picking keeps the name as free text.
 */
function VendorPicker({ value, onChange }) {
  const [q, setQ] = useState(value?.name || '');
  const [open, setOpen] = useState(false);
  const [sangly, setSangly] = useState([]);
  const [odoo, setOdoo] = useState([]);
  const [busy, setBusy] = useState(false);
  const req = useRef(0);

  // a prefilled / unlinked value from the parent → mirror it in the input
  useEffect(() => { setQ(value?.name || ''); }, [value?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const s = q.trim();
    if (s.length < 2 || value?.id) { setSangly([]); setOdoo([]); return undefined; }
    const my = ++req.current;
    const t = setTimeout(async () => {
      setBusy(true);
      try {
        const [local, remote] = await Promise.all([
          api.suppliers.list({ q: s, all: 1 }).then((d) => d.results || []).catch(() => []),
          api.suppliers.odooVendors(s).then((d) => (Array.isArray(d) ? d : [])).catch(() => []),
        ]);
        if (my !== req.current) return;
        setSangly(local.slice(0, 8));
        setOdoo(remote.filter((v) => !v.sangly_id).slice(0, 5));
      } finally { if (my === req.current) setBusy(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [q, value?.id]);

  const pickSangly = (s) => { onChange({ id: s.id, name: s.name }); setQ(s.name); setOpen(false); };
  const pickOdoo = async (v) => {
    setBusy(true);
    try {
      const sup = await api.suppliers.addVendor(v.partner_id, v.name);
      onChange({ id: sup.id, name: sup.name }); setQ(sup.name); setOpen(false);
    } catch { /* keep the typed name */ } finally { setBusy(false); }
  };
  const type = (v) => { setQ(v); onChange({ id: null, name: v }); setOpen(true); };
  const showMenu = open && !value?.id && q.trim().length >= 2;

  return (
    <div className="relative">
      <div className="relative">
        <Building2 className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => type(e.target.value)} onFocus={() => setOpen(true)}
               onBlur={() => setTimeout(() => setOpen(false), 150)}
               placeholder="Search a vendor… or type a name" className="pl-8 pr-8" />
        {value?.id
          ? <button type="button" title="Unlink" onClick={() => onChange({ id: null, name: q })}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-emerald-600 hover:text-rose-600"><Check className="h-4 w-4" /></button>
          : busy && <span className="absolute right-2.5 top-1/2 -translate-y-1/2"><PetanqueLoader size={14} /></span>}
      </div>
      <div className="mt-1 text-[11px] text-muted-foreground">
        {value?.id ? 'Linked to the vendor in Sangly.' : q.trim() ? 'Not linked — will be saved as a plain name.' : 'Linking lets the vendor page show this pro-forma.'}
      </div>
      {showMenu && (sangly.length > 0 || odoo.length > 0) && (
        <div className="absolute left-0 right-0 top-[42px] z-30 max-h-64 overflow-y-auto rounded-lg border border-border bg-popover p-1 shadow-pop">
          {sangly.map((s) => (
            <button key={`s${s.id}`} type="button" onMouseDown={(e) => { e.preventDefault(); pickSangly(s); }}
                    className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-[13px] hover:bg-muted">
              <span className="truncate">{s.name}</span>
              {s.open_count > 0 && <span className="ml-auto shrink-0 text-[11px] text-muted-foreground">{s.open_count} open</span>}
            </button>
          ))}
          {odoo.length > 0 && (
            <>
              <div className="mt-1 px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Odoo vendors · not in Sangly yet</div>
              {odoo.map((v) => (
                <button key={`o${v.partner_id}`} type="button" onMouseDown={(e) => { e.preventDefault(); pickOdoo(v); }}
                        className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-[13px] hover:bg-muted">
                  <span className="truncate">{v.name}</span>
                  <span className="ml-auto shrink-0 text-[11px] text-muted-foreground">add</span>
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function AddProformaDialog({ open, initialVendor, onClose, onCreated }) {
  const empty = {
    their_reference: '', our_reference: '', amount: '', currency: 'EUR', due_date: today(), remarks: '', gorgias_url: '',
  };
  const [form, setForm] = useState(empty);
  const [vendor, setVendor] = useState({ id: null, name: '' });
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const fileRef = useRef(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (open) { setForm(empty); setVendor(initialVendor || { id: null, name: '' }); setFiles([]); setErr(''); }
  }, [open, initialVendor]); // eslint-disable-line react-hooks/exhaustive-deps

  const addFiles = (list) => {
    const bad = list.find((f) => f.size > 10 * 1024 * 1024);
    if (bad) { setErr(`"${bad.name}" is too large - the limit is 10 MB per file.`); return; }
    setFiles((prev) => [...prev, ...list]);
    if (fileRef.current) fileRef.current.value = '';
  };

  const submit = async () => {
    if (!vendor.name.trim()) { setErr('Pick a vendor or type its name.'); return; }
    if (!(Number(form.amount) > 0)) { setErr('Enter an amount greater than 0.'); return; }
    setBusy(true); setErr('');
    try {
      const pf = await api.proformas.create({
        ...form,
        supplier: vendor.id ?? '',
        supplier_name: vendor.name.trim(),
        due_date: form.due_date || null,
      }, files);
      onCreated(pf);
    } catch (e) {
      const d = e?.response?.data;
      setErr(d?.detail || (d && Object.values(d).flat().join(' ')) || 'Could not save the pro-forma.');
    } finally { setBusy(false); }
  };

  const field = 'mb-1 block text-[12px] font-medium';

  return (
    <Dialog open={open} onClose={onClose} className="max-w-lg">
      <h2 className="mb-4 text-lg font-semibold">Add a pro-forma</h2>
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <label className={field}>Vendor <span className="text-rose-500">*</span></label>
          <VendorPicker value={vendor} onChange={setVendor} />
        </div>
        <div>
          <label className={field}>Amount <span className="text-rose-500">*</span></label>
          <Input type="number" min="0" step="0.01" value={form.amount} onChange={set('amount')} placeholder="0.00" />
        </div>
        <div>
          <label className={field}>Currency</label>
          <Select value={form.currency} onChange={set('currency')} className="w-full">
            {CURRENCIES.map(([code, name]) => <option key={code} value={code}>{code} · {name}</option>)}
          </Select>
        </div>
        <div>
          <label className={field}>Their order no.</label>
          <Input value={form.their_reference} onChange={set('their_reference')} placeholder="On their pro-forma - the payment ref" className="font-mono" />
        </div>
        <div>
          <label className={field}>Our PO no.</label>
          <Input value={form.our_reference} onChange={set('our_reference')} placeholder="PO00921" className="font-mono" />
        </div>
        <div>
          <label className={field}>Deadline</label>
          <Input type="date" value={form.due_date} onChange={set('due_date')} />
        </div>
        <div>
          <label className={field}>Pro-forma file (PDF)</label>
          <input ref={fileRef} type="file" hidden multiple accept={ACCEPT}
                 onChange={(e) => addFiles(Array.from(e.target.files || []))} />
          <button type="button" onClick={() => fileRef.current?.click()}
            className="inline-flex h-10 w-full items-center justify-center gap-1.5 rounded-md border border-dashed border-border bg-card px-2 text-[12px] font-medium text-muted-foreground hover:bg-muted">
            <Paperclip className="h-3.5 w-3.5" /> {files.length ? 'Add another file' : 'Attach the PDF'}
          </button>
        </div>
        {files.length > 0 && (
          <div className="col-span-2 flex flex-wrap gap-1.5">
            {files.map((f, i) => (
              <span key={`${f.name}-${i}`} className="inline-flex max-w-[240px] items-center gap-1.5 rounded-md border border-border bg-card px-2 py-1 text-[12px]">
                <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span className="truncate">{f.name}</span>
                <button type="button" onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}
                        className="shrink-0 text-muted-foreground hover:text-rose-600"><X className="h-3 w-3" /></button>
              </span>
            ))}
          </div>
        )}
        <div className="col-span-2">
          <label className={field}>Gorgias ticket</label>
          <Input value={form.gorgias_url} onChange={set('gorgias_url')} placeholder="Paste the ticket URL - lets you reply to the vendor from here" />
        </div>
        <div className="col-span-2">
          <label className={field}>Extra information</label>
          <textarea value={form.remarks} onChange={set('remarks')} rows={2}
            placeholder="What it's for, payment terms, anything the payer should know…"
            className="w-full rounded-md border border-border bg-card px-2.5 py-2 text-[13px] focus:outline-none focus:ring-1 focus:ring-primary" />
        </div>
      </div>
      {err && <div className="mt-3 text-[13px] text-rose-600">{err}</div>}
      <div className="mt-5 flex items-center justify-end gap-2">
        <button onClick={onClose} className="text-[13px] text-muted-foreground hover:underline">Cancel</button>
        <Button onClick={submit} disabled={busy}>
          {busy ? <PetanqueLoader size={16} /> : <Send className="h-4 w-4" />} Add pro-forma
        </Button>
      </div>
    </Dialog>
  );
}


/**
 * "Check Airwallex" (payers only): a fresh login against the configured
 * environment, the EUR balance, and the webhook configuration - the quickest
 * way to see that a new key / base URL / IP allowlist actually works.
 */
function AirwallexCheck() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [err, setErr] = useState('');

  const run = async () => {
    setBusy(true); setErr('');
    try { setRes(await api.proformas.airwallexStatus()); }
    catch (e) { setErr(e?.response?.data?.detail || 'Could not run the check.'); }
    finally { setBusy(false); }
  };
  useEffect(() => { if (open) run(); }, [open]);

  const Row = ({ ok, label, detail }) => {
    const Icon = ok === true ? CheckCircle2 : ok === false ? XCircle : MinusCircle;
    const tone = ok === true ? 'text-emerald-600' : ok === false ? 'text-rose-600' : 'text-muted-foreground';
    return (
      <div className="flex items-start gap-2 py-1.5 text-[13px]">
        <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', tone)} />
        <div className="min-w-0"><span className="font-medium">{label}</span>{detail && <span className="text-muted-foreground"> · {detail}</span>}</div>
      </div>
    );
  };

  return (
    <>
      <button onClick={() => setOpen(true)} title="Check the Airwallex connection"
        className="inline-flex h-9 items-center gap-1.5 rounded-md border border-border bg-card px-3 text-[13px] hover:bg-muted">
        <Zap className="h-4 w-4 text-primary" /> Check Airwallex
      </button>
      <Dialog open={open} onClose={() => setOpen(false)} className="max-w-md">
        <h2 className="mb-1 text-lg font-semibold">Airwallex connection</h2>
        <p className="mb-3 text-[12px] text-muted-foreground">Logs in afresh with the configured key and reads the EUR balance.</p>
        {busy && !res ? (
          <div className="grid h-32 place-items-center"><PetanqueLoader size={30} roll /></div>
        ) : err ? (
          <div className="text-[13px] text-rose-600">{err}</div>
        ) : res && (
          <div className="divide-y divide-border/60">
            <Row ok={res.enabled} label={res.enabled ? 'Keys configured' : 'Keys not configured'}
                 detail={res.enabled ? `${res.environment} · ${res.host}${res.account_id_set ? ' · account id set' : ''}` : 'AIRWALLEX_CLIENT_ID / AIRWALLEX_API_KEY missing on the backend'} />
            {res.enabled && (
              <Row ok={res.login} label={res.login ? 'Login OK' : 'Login failed'}
                   detail={res.login ? 'key, base URL and IP allowlist are right' : res.error} />
            )}
            {res.login && (
              <Row ok={res.balance != null} label="EUR balance (payments are funded from it)"
                   detail={res.balance != null ? `available ${moneyExact(res.balance, 'EUR')}` : 'not readable (Balances scope?)'} />
            )}
            {res.login && res.balances?.length > 0 && (
              <div className="py-1.5 text-[12px] text-muted-foreground">
                Funded balances: {res.balances.map((b) => moneyExact(b.available, b.currency)).join(' · ')}
              </div>
            )}
            {res.enabled && (
              <div className="py-1.5 text-[12px] text-muted-foreground">
                Sender shown to vendors: <span className="font-medium text-foreground">{res.account_label || 'not set'}</span>
                {!res.account_label && ' - set AIRWALLEX_ACCOUNT_LABEL to the legal entity name (Settings > Account in Airwallex)'}
              </div>
            )}
            <Row ok={res.webhook_secret_set ? true : null} label={res.webhook_secret_set ? 'Webhook secret set' : 'Webhook secret not set'}
                 detail={res.webhook_secret_set ? 'status events will be applied' : 'status is picked up by polling only'} />
            <div className="py-1.5 text-[12px] text-muted-foreground">
              Webhook URL: <span className="select-all font-mono">{res.webhook_url}</span><br />
              Open it in a browser: it must answer <span className="font-mono">{'{"ok": true}'}</span>. Then use “Send test event” in Airwallex; a delivered event shows as 200 there.
            </div>
            <div className="py-1.5 text-[12px] text-muted-foreground">
              {res.beneficiaries_saved} vendor{res.beneficiaries_saved === 1 ? '' : 's'} with a saved beneficiary · {res.in_payment} in payment · {res.paid_via_airwallex} paid via Airwallex
            </div>
          </div>
        )}
        <div className="mt-4 flex items-center gap-3">
          <Button size="sm" variant="secondary" onClick={run} disabled={busy}>
            <RefreshCw className={cn('h-3.5 w-3.5', busy && 'animate-spin')} /> Re-check
          </Button>
          <button onClick={() => setOpen(false)} className="ml-auto text-[13px] text-muted-foreground hover:underline">Close</button>
        </div>
      </Dialog>
    </>
  );
}
